/**
 * Version checker — detects new deploys by comparing the hashed main JS bundle
 * filename in the currently loaded page vs a fresh fetch of /index.html.
 *
 * Also triggers SW registration.update() in parallel.
 *
 * When a new version is detected, dispatches 'sw-update-available' on window.
 */
import { canAutoReload } from './updateGuard';

const CHECK_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

let _initialBundle = null;
let _updateDetected = false;
let _pendingAutoReload = false;
let _swRegistration = null;
let _intervalId = null;

/** Extract the main JS bundle filename from raw HTML string */
function extractBundleName(html) {
  // CRA produces: <script src="/static/js/main.HASH.js">
  const m = html.match(/\/static\/js\/main\.[a-f0-9]+\.js/i);
  return m ? m[0] : null;
}

/** Capture the bundle loaded by the CURRENT page */
function captureInitialBundle() {
  // From the live DOM — the <script> tags in the document
  const scripts = document.querySelectorAll('script[src]');
  for (const s of scripts) {
    const src = s.getAttribute('src') || '';
    if (/\/static\/js\/main\.[a-f0-9]+\.js/i.test(src)) {
      _initialBundle = src;
      return;
    }
  }
  // Fallback: performance entries
  if (window.performance?.getEntriesByType) {
    for (const e of performance.getEntriesByType('resource')) {
      if (/\/static\/js\/main\.[a-f0-9]+\.js/i.test(e.name)) {
        _initialBundle = new URL(e.name).pathname;
        return;
      }
    }
  }
}

function dispatchUpdate(worker) {
  if (_updateDetected) return; // Only fire once per session
  // If we already reloaded for this bundle, don't show banner again
  // sessionStorage: per-tab, so one tab reloading won't suppress the banner on other tabs
  const reloadedFor = sessionStorage.getItem('ft_reloaded_for_bundle');
  if (reloadedFor && reloadedFor === _initialBundle) return;
  _updateDetected = true;
  console.log('[VersionCheck] New version detected — dispatching update event');
  window.dispatchEvent(new CustomEvent('sw-update-available', { detail: { worker: worker || null } }));
}

/** Fetch /index.html and compare bundle filename */
async function checkBundleVersion() {
  try {
    const resp = await fetch(`/index.html?_t=${Date.now()}`, { cache: 'no-store' });
    if (!resp.ok) return;
    const html = await resp.text();
    const remoteBundleName = extractBundleName(html);
    if (!remoteBundleName || !_initialBundle) return;
    if (remoteBundleName !== _initialBundle) {
      dispatchUpdate(null);
    }
  } catch {
    // Network error — ignore silently
  }
}

/** Trigger SW update check */
async function checkSWUpdate() {
  if (!_swRegistration) return;
  try {
    await _swRegistration.update();
  } catch {
    // SW update check failed — ignore
  }
}

/** Run both checks */
function runChecks() {
  checkBundleVersion();
  checkSWUpdate();
}

/** Handle visibility / focus — run checks + auto-reload if pending */
function onVisible() {
  if (document.visibilityState !== 'visible') return;
  runChecks();
  if (_pendingAutoReload && canAutoReload()) {
    window.location.reload();
  }
}

function onFocus() {
  runChecks();
  if (_pendingAutoReload && canAutoReload()) {
    window.location.reload();
  }
}

/** Return the bundle path captured at boot (e.g. '/static/js/main.abc123.js') or null */
export function getInitialBundle() {
  return _initialBundle;
}

/** Mark that an auto-reload should happen on next safe visibility */
export function setPendingAutoReload() {
  _pendingAutoReload = true;
}

/** Start the version checker. Call once at app boot. */
export function initVersionChecker() {
  captureInitialBundle();
  // Clear the reload-guard if we're now running a new bundle
  try {
    const prev = sessionStorage.getItem('ft_reloaded_for_bundle');
    if (prev && prev !== _initialBundle) sessionStorage.removeItem('ft_reloaded_for_bundle');
  } catch {}
  console.log('[VersionCheck] Initial bundle:', _initialBundle);

  // SW registration
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    navigator.serviceWorker.register('/service-worker.js')
      .then((reg) => {
        _swRegistration = reg;
        console.log('SW registered:', reg.scope);

        function onNewWorkerWaiting(worker) {
          if (!navigator.serviceWorker.controller) return;
          dispatchUpdate(worker);
        }

        if (reg.waiting) onNewWorkerWaiting(reg.waiting);

        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              onNewWorkerWaiting(newWorker);
            }
          });
        });
      })
      .catch((err) => console.error('SW registration failed:', err));

    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing || window.__sw_reloaded) return;
      refreshing = true;
      window.__sw_reloaded = true;
      window.location.reload();
    });
  }

  // Initial check (after a short delay to let the page settle)
  setTimeout(runChecks, 3000);

  // Visibility + focus listeners
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onFocus);

  // 5-minute polling interval — paused when hidden
  _intervalId = setInterval(() => {
    if (!document.hidden) runChecks();
  }, CHECK_INTERVAL_MS);
}
